import React, { useState, useEffect } from 'react'
import { Card, Select, Switch, Button, message, Modal, Checkbox } from 'antd'
import styled from 'styled-components'
import moment from 'moment'
import { useAuth } from '../hooks/AuthContext'
import api from '../utils/api'
import {
  ClockCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  CopyOutlined
} from '@ant-design/icons'

const { Option } = Select

const Container = styled.div`
  max-width: 1000px;
  margin: auto;
  padding: 10px 0;
  background: #fff;
  border-radius: 12px;

  @media (max-width: 768px) {
    padding-bottom: 80px;
  }
`

const PageHeader = styled.div`
  margin-bottom: 30px;
  h2 {
    margin-top: 0;
    font-size: 24px;
    color: #1a1a1a;
    display: flex;
    align-items: center;
    gap: 10px;
    .anticon {
      color: #4096ff;
    }
  }
`

const DayCard = styled(Card)`
  margin-bottom: 20px;
  border-radius: 8px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.03);

  .ant-card-body {
    padding: 20px;
  }

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
`

const DayHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;

  h3 {
    font-size: 18px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .ant-switch {
    background-color: #bfbfbf;
    &.ant-switch-checked {
      background-color: #4caf50;
    }
  }
`

const TimePeriodContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  margin-bottom: 16px;
  padding: 12px;
  background: #f8f9fa;
  border-radius: 8px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;

    .time-group {
      display: flex;
      align-items: center;
      margin-bottom: 10px;
      width: 100%;

      .ant-select {
        flex: 1;
      }
    }
  }

  .time-separator {
    margin: 0 12px;
    color: #666;
  }
`

const ActionButtons = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;

  @media (max-width: 768px) {
    justify-content: flex-end;
    margin-top: 10px;
  }
`

const DayActions = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 12px;

  .apply-all-btn {
    color: #4caf50;
    &:hover {
      color: #fff;
      background: #4caf50;
      border-color: #4caf50;
    }
  }
`

const AddPeriodButton = styled(Button)`
  width: 100%;
  height: 44px;
  margin-top: 16px;
  border-radius: 8px;
  font-size: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;

  &:hover {
    background: #f6f6f6;
  }

  .anticon {
    font-size: 16px;
  }
`

const IconButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;

  &.delete-btn {
    color: #ff4d4f;
    &:hover {
      color: #fff;
      background: #ff4d4f;
      border-color: #ff4d4f;
    }
  }

  &.apply-btn {
    color: #4caf50;
    &:hover {
      color: #fff;
      background: #4caf50;
      border-color: #4caf50;
    }
  }
`

const StyledModal = styled(Modal)`
  .ant-modal-content {
    border-radius: 12px;

    .ant-modal-header {
      border-radius: 12px 12px 0 0;
    }
  }

  .ant-checkbox-group {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 12px;
    margin-top: 12px;
  }
`

const ButtonGroup = styled.div`
  margin-top: 20px;
  display: flex;
  justify-content: center;

  @media (max-width: 768px) {
    display: none;
  }
`

const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
  font-size: 16px;
`

const ApplyButton = styled(Button)`
  margin-left: 10px;
  background-color: #4caf50; // 綠色背景
  color: white;
  &:hover {
    background-color: #45a049; // 濃綠色
  }
`

const FixedButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 16px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(4px);
  z-index: 1000;

  > div {
    max-width: 1000px;
    margin: 0 auto;
  }

  @media (min-width: 769px) {
    display: none;
  }
`

const SaveButton = styled(Button)`
  width: 100%;
  height: 44px;
  font-size: 16px;
  font-weight: 500;
  background: #4caf50;
  border: none;

  &:hover,
  &:focus {
    background: #45a049;
  }

  &.shake {
    animation: shake 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
  }

  @keyframes shake {
    10%,
    90% {
      transform: translate3d(-1px, 0, 0);
    }
    20%,
    80% {
      transform: translate3d(2px, 0, 0);
    }
    30%,
    50%,
    70% {
      transform: translate3d(-4px, 0, 0);
    }
    40%,
    60% {
      transform: translate3d(4px, 0, 0);
    }
  }
`

const generateTimeOptions = (interval) => {
  const times = []
  const start = moment().startOf('day')
  const end = moment().endOf('day')
  while (start <= end) {
    times.push(start.format('HH:mm'))
    start.add(interval, 'minutes')
  }
  return times
}

// 將這些常量定義放在組件外部
const chineseDays = {
  Monday: '星期一',
  Tuesday: '星期二',
  Wednesday: '星期三',
  Thursday: '星期四',
  Friday: '星期五',
  Saturday: '星期六',
  Sunday: '星期日'
}

const englishDays = {
  星期一: 'Monday',
  星期二: 'Tuesday',
  星期三: 'Wednesday',
  星期四: 'Thursday',
  星期五: 'Friday',
  星期六: 'Saturday',
  星期日: 'Sunday'
}

const defaultBusinessHours = {
  星期一: { enabled: true, periods: [{ start: '10:00', end: '19:00' }] },
  星期二: { enabled: true, periods: [{ start: '10:00', end: '19:00' }] },
  星期三: { enabled: true, periods: [{ start: '10:00', end: '19:00' }] },
  星期四: { enabled: true, periods: [{ start: '10:00', end: '19:00' }] },
  星期五: { enabled: true, periods: [{ start: '10:00', end: '19:00' }] },
  星期六: { enabled: true, periods: [{ start: '10:00', end: '19:00' }] },
  星期日: { enabled: true, periods: [{ start: '11:00', end: '15:00' }] }
}

const BusinessHours = () => {
  const [defaultInterval, setDefaultInterval] = useState(30)
  const [businessHours, setBusinessHours] = useState(defaultBusinessHours)
  const { user } = useAuth()
  const businessId = user.businessId

  useEffect(() => {
    api
      .get(`/api/business_B/${businessId}/hours`)
      .then((response) => {
        const data = response.data.data
        if (
          !data.opening_hours ||
          Object.keys(data.opening_hours).length === 0
        ) {
          setBusinessHours(defaultBusinessHours)
        } else {
          // 将英文星期名转换为中文
          const translatedHours = Object.entries(data.opening_hours).reduce(
            (acc, [day, value]) => {
              acc[chineseDays[day] || day] = value
              return acc
            },
            {}
          )
          setBusinessHours(translatedHours)
          setDefaultInterval(data.serviceInterval)
        }
      })
      .catch((error) => {
        console.error('Error fetching business hours:', error)
      })
  }, [businessId])

  const handleIntervalChange = (value) => {
    setDefaultInterval(value)
  }

  const handleSwitchChange = (day) => {
    setBusinessHours((prev) => ({
      ...prev,
      [day]: { ...prev[day], enabled: !prev[day].enabled }
    }))
  }

  const handleTimeChange = (day, index, field, time) => {
    const timeStr = time ? time.format('HH:mm') : ''
    setBusinessHours((prev) => {
      const periods = [...prev[day].periods]
      periods[index][field] = timeStr
      return { ...prev, [day]: { ...prev[day], periods } }
    })
  }

  const handleSave = () => {
    // 将中文星期转换回英文，并转换为正确的对象格式
    const englishBusinessHours = Object.entries(businessHours).reduce(
      (acc, [day, value]) => {
        const englishDay = englishDays[day] || day
        acc[englishDay] = {
          enabled: value.enabled,
          periods: value.periods
        }
        return acc
      },
      {}
    )

    api
      .put(`/api/business_B/${businessId}/hours`, {
        opening_hours: englishBusinessHours,
        serviceInterval: defaultInterval
      })
      .then((response) => {
        message.success('營業時間已更新')
      })
      .catch((error) => {
        message.error('更新失敗，請稍後再試')
        console.error('Error updating business hours:', error)
      })
  }

  const timeOptions = generateTimeOptions(defaultInterval).map((time) => (
    <Option key={time} value={time}>
      {moment(time, 'HH:mm').format('h:mm A')}
    </Option>
  ))

  // 新增一個狀態來儲存選擇的時間
  const [selectedTime, setSelectedTime] = useState({ start: '', end: '' })

  // 新增一個函數來處理應用到其他日子的邏輯
  const handleApplyToOtherDays = (day) => {
    const updatedBusinessHours = { ...businessHours }
    Object.keys(updatedBusinessHours).forEach((d) => {
      if (d !== day && updatedBusinessHours[d].enabled) {
        updatedBusinessHours[d].periods.push(selectedTime)
      }
    })
    setBusinessHours(updatedBusinessHours)
  }

  // 新增狀態來控制模態框的顯示
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [selectedDays, setSelectedDays] = useState([])

  // 新增一個函數來處理顯示模態框
  const showApplyModal = (day, periods) => {
    setCurrentDay(day)
    setSelectedTime(periods) // 儲存整天的時段
    setSelectedDays([])
    setIsModalVisible(true)
  }

  // 修改 handleApplySubmit 函數
  const handleApplySubmit = () => {
    const updatedBusinessHours = { ...businessHours }

    if (selectedDays.length === 0) {
      // 套用到所有其他啟用的日期
      Object.keys(updatedBusinessHours).forEach((day) => {
        if (day !== currentDay && updatedBusinessHours[day].enabled) {
          updatedBusinessHours[day].periods = selectedTime.map((period) => ({
            start: period.start,
            end: period.end
          }))
        }
      })
    } else {
      // 套用到選擇的日期
      selectedDays.forEach((day) => {
        const chineseDay = chineseDays[day]
        if (chineseDay && updatedBusinessHours[chineseDay]?.enabled) {
          updatedBusinessHours[chineseDay].periods = selectedTime.map(
            (period) => ({
              start: period.start,
              end: period.end
            })
          )
        }
      })
    }

    setBusinessHours(updatedBusinessHours)
    setIsModalVisible(false)
    message.success('時段套用成功')
  }

  const handleDeletePeriod = (day, index) => {
    setBusinessHours((prev) => {
      const newPeriods = [...prev[day].periods]
      newPeriods.splice(index, 1)
      return {
        ...prev,
        [day]: {
          ...prev[day],
          periods: newPeriods
        }
      }
    })
  }

  const handleAddPeriod = (day) => {
    const lastPeriod = businessHours[day]?.periods?.slice(-1)[0]
    const defaultStart = lastPeriod?.start ?? '10:00'
    const defaultEnd = lastPeriod?.end ?? '19:00'
    setBusinessHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        periods: [...(prev[day].periods || []), { start: defaultStart, end: defaultEnd }]
      }
    }))
  }

  // 新增狀態
  const [currentDay, setCurrentDay] = useState('')

  // 添加全選處理函數
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      // 選擇所有未被禁用的選項
      const availableDays = Object.entries(chineseDays)
        .filter(([eng, chi]) => chi !== currentDay)
        .map(([eng]) => eng)
      setSelectedDays(availableDays)
    } else {
      setSelectedDays([])
    }
  }

  // 添加狀態來追踪是否有未保存的更改
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // 在任何更改時設置未保存狀態
  useEffect(() => {
    setHasUnsavedChanges(true)
  }, [businessHours, defaultInterval])

  // 添加離開頁面提醒
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges) {
        e.preventDefault()
        e.returnValue = '您有未保存的更改，確定要離開嗎？'
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges])

  return (
    <Container>
      {/* 時間間隔選擇 */}
      <Card
        title={
          <span>
            <ClockCircleOutlined /> 預設服務時間間隔
          </span>
        }
        style={{ marginBottom: '24px' }}
      >
        <Select
          value={defaultInterval}
          onChange={handleIntervalChange}
          style={{ width: '100%' }}
        >
          {[
            5, 10, 15, 20, 30, 45, 60, 90, 120, 150, 180, 210, 240, 270, 300,
            330, 360, 390, 420, 450, 480
          ].map((interval) => (
            <Option key={interval} value={interval}>
              {interval < 60
                ? `${interval} 分鐘`
                : `${Math.floor(interval / 60)} 小時${
                    interval % 60 ? ` ${interval % 60} 分鐘` : ''
                  }`}
            </Option>
          ))}
        </Select>
      </Card>

      {/* 營業時間卡片 */}
      {Object.entries(businessHours).map(([day, { enabled, periods }]) => (
        <DayCard key={day}>
          <DayHeader>
            <h3>{day}</h3>
            <Switch
              checked={enabled}
              onChange={() => handleSwitchChange(day)}
            />
          </DayHeader>
          {enabled && (
            <>
              {periods.map((period, index) => (
                <TimePeriodContainer key={index}>
                  <div className='time-group'>
                    <Select
                      value={period.start}
                      onChange={(value) =>
                        handleTimeChange(
                          day,
                          index,
                          'start',
                          moment(value, 'HH:mm')
                        )
                      }
                      style={{ width: '150px' }}
                    >
                      {timeOptions}
                    </Select>
                    <span className='time-separator'>至</span>
                    <Select
                      value={period.end}
                      onChange={(value) =>
                        handleTimeChange(
                          day,
                          index,
                          'end',
                          moment(value, 'HH:mm')
                        )
                      }
                      style={{ width: '150px' }}
                    >
                      {timeOptions}
                    </Select>
                  </div>
                  <ActionButtons>
                    <IconButton
                      className='delete-btn'
                      icon={<DeleteOutlined />}
                      onClick={() => handleDeletePeriod(day, index)}
                    />
                  </ActionButtons>
                </TimePeriodContainer>
              ))}
              <AddPeriodButton type='dashed' onClick={() => handleAddPeriod(day)}>
                <PlusOutlined /> 新增時段
              </AddPeriodButton>
              <DayActions>
                <IconButton
                  className='apply-all-btn'
                  icon={<CopyOutlined />}
                  onClick={() => showApplyModal(day, periods)}
                >
                  套用到其他時段
                </IconButton>
              </DayActions>
            </>
          )}
        </DayCard>
      ))}

      <StyledModal
        title='套用時段'
        open={isModalVisible}
        onOk={handleApplySubmit}
        onCancel={() => setIsModalVisible(false)}
        okText='確認'
        cancelText='取消'
      >
        <p>請選擇要被套用的星期：</p>
        <Checkbox
          onChange={handleSelectAll}
          checked={
            selectedDays.length ===
            Object.keys(chineseDays).filter(
              (day) => chineseDays[day] !== currentDay
            ).length
          }
          indeterminate={
            selectedDays.length > 0 &&
            selectedDays.length <
              Object.keys(chineseDays).filter(
                (day) => chineseDays[day] !== currentDay
              ).length
          }
        >
          全選
        </Checkbox>
        <Checkbox.Group
          options={Object.entries(chineseDays).map(([eng, chi]) => ({
            label: chi,
            value: eng,
            disabled: chi === currentDay
          }))}
          value={selectedDays}
          onChange={setSelectedDays}
        />
      </StyledModal>

      {/* 桌面版按鈕 */}
      <ButtonGroup>
        <SaveButton type='primary' onClick={handleSave}>
          {hasUnsavedChanges ? '如有修改請點擊儲存' : '儲存設定'}
        </SaveButton>
      </ButtonGroup>

      {/* 手機版固定底部按鈕 */}
      <FixedButtonContainer>
        <div>
          <SaveButton
            type='primary'
            onClick={handleSave}
            className={hasUnsavedChanges ? 'shake' : ''}
          >
            {hasUnsavedChanges ? '如有修改請點擊儲存' : '儲存設定'}
          </SaveButton>
        </div>
      </FixedButtonContainer>
    </Container>
  )
}

export default BusinessHours
